import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { useFormikContext } from "formik";
import ToolTip from "components/design/toolTip";
import { FormikForm } from "components/FormikForm/FormikForm";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { CircularProgress } from "components/DesignSystem/CircularProgress/CircularProgress";
import ProgressBar from "components/DesignSystem/ProgressBar/ProgressBar";
import { colors } from "constants/colors";
import { Headset } from "components/icons/Headset";
import { ONBOARDING_LINK } from "constants/meetingRequestUrls";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { MultiValue, SingleValue } from "react-select";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { useGetEntitiesQuery } from "store/apis/group";
import { useGetGroupReviewDetailsQuery } from "v2/store/onboardingEntityApi";
import { useGroupId } from "v2/utils/useGroupId";
import {
  useGetTaxYearsQuery,
  useGetHomepageQuery,
  usePutHomepageMutation,
  usePatchHomepageMutation,
  useUpload7004FiledCopyMutation,
  useGetDetectedFilingsQuery,
  useGetDetectedFilingsCountQuery,
} from "v2/store/taxSetupApi";
import { buildScopePayload, fromScopePayload } from "v2/utils/taxSetupPayload";
import {
  buildMultiYearQuestions,
  buildTaxSetupInitialValues,
  MY_SAME_QUESTION_ID,
  REQUIRED_QUESTION_IDS,
  SAME_AS_CURRENT,
  TAX_ONBOARDING_ENTITIES_KEY,
  TAX_SETUP_QUESTIONS,
  TaxSetupQuestion as TaxSetupQuestionType,
  TaxSetupValues,
} from "v2/constants/taxSetupQuestions";
import { groupFilingsBySeason } from "v2/utils/mapDetectedFilings";
import { useOnboardingProgress } from "v2/utils/useOnboardingProgress";
import { isQuestionAnswered } from "v2/utils/isQuestionAnswered";
import { isSeasonComplete } from "v2/utils/taxSetupProgress";
import { FlightRect, useFilingFlights } from "v2/utils/useFilingFlights";
import { getFilingRowRect } from "v2/utils/getFilingRowRect";
import { scrollFilingRowIntoView } from "v2/utils/scrollFilingRowIntoView";
import { FilingFlightLayer } from "./FilingFlightLayer";
import { FilingsDetectedPanel } from "./FilingsDetectedPanel";
import { SeasonAccordion } from "./SeasonAccordion";
import { SetupDocumentsBox } from "./SetupDocumentsBox";
import { SetupReviewBanner } from "./SetupReviewBanner";
import { TaxSetupQuestion } from "./TaxSetupQuestion";
import { TaxSetupSections } from "./TaxSetupSections";

export type SetupEntity = { id: string; name: string };

type OnboardingEntity = { entityId: string; name: string };

type SetupModalProps = {
  open: boolean;
  onClose: () => void;
  entities: SetupEntity[];

  seasons?: number[];

  incompleteEntityIds?: string[];

  focusEntityId?: string | null;
};

const EMPTY_INCOMPLETE_IDS: string[] = [];
const EMPTY_SEASONS: number[] = [];

const MODAL_DESCRIPTION =
  "Enter your business information below. We'll use these details to identify the filings that apply to your business.";

const GENERIC_ERROR = "Something went wrong";
const AUTOSAVE_DEBOUNCE_MS = 800;
const QUESTION_ATTRIBUTE = "data-question-id";
const QUESTION_SELECTOR = `[${QUESTION_ATTRIBUTE}]`;

const sortDesc = (a: number, b: number): number => b - a;

const scopePrefix = (entityId: string, season: number): string =>
  `${entityId}__${season}__`;

const questionsForSeason = (
  season: number,
  currentSeason: number
): TaxSetupQuestionType[] =>
  season === currentSeason
    ? TAX_SETUP_QUESTIONS
    : buildMultiYearQuestions(season, currentSeason);

const buildInitialValues = (
  entities: SetupEntity[],
  seasons: number[],
  currentSeason: number
): TaxSetupValues => {
  const values: TaxSetupValues = {};

  seasons.forEach((season) => {
    const base = buildTaxSetupInitialValues(
      questionsForSeason(season, currentSeason)
    );

    entities.forEach((entity) => {
      const prefix = scopePrefix(entity.id, season);
      Object.entries(base).forEach(([key, value]) => {
        values[`${prefix}${key}`] = Array.isArray(value) ? [] : "";
      });
    });
  });

  return values;
};

const pickScopeValues = (
  values: TaxSetupValues,
  prefix: string
): TaxSetupValues => {
  const scoped: TaxSetupValues = {};

  Object.entries(values).forEach(([key, value]) => {
    if (key.startsWith(prefix)) {
      scoped[key.slice(prefix.length)] = value;
    }
  });

  return scoped;
};

const resolveSeasonScope = (
  values: TaxSetupValues,
  entityId: string,
  season: number,
  currentSeason: number
): TaxSetupValues => {
  const scope = pickScopeValues(values, scopePrefix(entityId, season));
  if (season === currentSeason) return scope;

  const currentScope = pickScopeValues(
    values,
    scopePrefix(entityId, currentSeason)
  );

  if (scope.my_same === SAME_AS_CURRENT) return { my_same: SAME_AS_CURRENT };

  const resolved: TaxSetupValues = {};
  Object.entries(scope).forEach(([key, value]) => {
    if (key === MY_SAME_QUESTION_ID) {
      resolved[key] = value;
      return;
    }
    resolved[key] = value === SAME_AS_CURRENT ? currentScope[key] : value;
  });

  return resolved;
};

const stableStringify = (payload: Record<string, unknown>): string =>
  JSON.stringify(payload, Object.keys(payload).sort());

type HomepageSyncProps = {
  groupId: string;
  apiEntityId: string;
  scopeEntityId: string;
  seasons: number[];
  currentSeason: number;
};

type SeasonPatch = {
  season: number;
  payload: ReturnType<typeof buildScopePayload>;
  serialized: string;
};

const HomepagePrefill = ({
  groupId,
  apiEntityId,
  scopeEntityId,
  seasons,
}: HomepageSyncProps) => {
  const { values, setFieldValue } = useFormikContext<TaxSetupValues>();

  const { data } = useGetHomepageQuery(
    { groupId, entityId: apiEntityId, seasons },
    { skip: !groupId || !apiEntityId }
  );

  const latestValues = useRef(values);
  latestValues.current = values;

  useEffect(() => {
    if (!data) return;

    Object.entries(data).forEach(([season, answers]) => {
      const scoped = fromScopePayload(answers);

      Object.entries(scoped).forEach(([questionId, value]) => {
        if (!isQuestionAnswered(value)) return;

        const fieldName = `${scopeEntityId}__${season}__${questionId}`;
        if (isQuestionAnswered(latestValues.current[fieldName])) return;

        setFieldValue(fieldName, value, false);
      });
    });
  }, [data, scopeEntityId, setFieldValue]);

  return null;
};

const HomepageAutosave = ({
  groupId,
  apiEntityId,
  scopeEntityId,
  seasons,
  currentSeason,
}: HomepageSyncProps) => {
  const { values } = useFormikContext<TaxSetupValues>();
  const { alertToast } = useToast();

  const { data: serverData, isError } = useGetHomepageQuery(
    { groupId, entityId: apiEntityId, seasons },
    { skip: !groupId || !apiEntityId }
  );

  const [patchHomepage] = usePatchHomepageMutation();

  const lastSaved = useRef<Record<number, string>>({});
  const isSeeded = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pending = useRef<SeasonPatch[]>([]);

  const commit = useCallback(
    (entries: SeasonPatch[]): void => {
      entries.forEach(async (entry) => {
        try {
          await patchHomepage({
            groupId,
            entityId: apiEntityId,
            seasons,
            payload: { [entry.season]: entry.payload },
          }).unwrap();
          lastSaved.current[entry.season] = entry.serialized;
        } catch (error) {
          const message =
            (error as { data?: { message?: string } })?.data?.message ??
            GENERIC_ERROR;
          alertToast({ message });
        }
      });
    },
    [patchHomepage, groupId, apiEntityId, seasons, alertToast]
  );

  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    if (isSeeded.current) return;
    if (!serverData && !isError) return;

    seasons.forEach((season) => {
      const scope = serverData
        ? fromScopePayload(serverData[season] ?? {})
        : resolveSeasonScope(values, scopeEntityId, season, currentSeason);
      lastSaved.current[season] = stableStringify(
        buildScopePayload(scope, season !== currentSeason)
      );
    });
    isSeeded.current = true;
  }, [serverData, isError, seasons, values, scopeEntityId, currentSeason]);

  useEffect(() => {
    if (!isSeeded.current) return undefined;

    const changed = seasons
      .map((season) => {
        const scope = resolveSeasonScope(
          values,
          scopeEntityId,
          season,
          currentSeason
        );
        const payload = buildScopePayload(scope, season !== currentSeason);
        return { season, payload, serialized: stableStringify(payload) };
      })
      .filter((entry) => entry.serialized !== lastSaved.current[entry.season]);

    pending.current = changed;

    if (changed.length === 0) return undefined;

    clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      commitRef.current(changed);
      pending.current = [];
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer.current);
  }, [values, seasons, scopeEntityId, currentSeason]);

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      if (pending.current.length > 0) {
        commitRef.current(pending.current);
        pending.current = [];
      }
    },
    []
  );

  return null;
};

const SetupProgress = ({ value }: { value: number }) => (
  <ProgressBar.Root
    value={value}
    type="grey"
    className="!t-h-1 t-shrink-0 !t-rounded-none"
  >
    <ProgressBar.Indicator
      progress={value}
      className="t-bg-setup-progress !t-rounded-none !t-bg-surface-transparent"
    />
  </ProgressBar.Root>
);

type EntityRailProps = {
  entities: SetupEntity[];
  selectedId: string;
  progressById: Record<string, number>;
  onSelect: (id: string) => void;
};

const EntityRail = ({
  entities,
  selectedId,
  progressById,
  onSelect,
}: EntityRailProps) => (
  <div className="hide-scrollbar t-flex t-w-60 t-shrink-0 t-flex-col t-overflow-y-auto t-border-0 t-border-r t-border-solid t-border-neutral-0">
    {entities.map((entity) => {
      const isSelected = entity.id === selectedId;

      const itemClassName = classNames(
        "t-flex t-w-full t-shrink-0 !t-h-auto t-items-center !t-justify-start t-gap-3 !t-rounded-none t-border-0 t-border-b t-border-solid t-border-neutral-0 !t-px-4 !t-py-3 t-text-left !t-text-body !t-text-text-60",
        isSelected ? "!t-bg-surface-purple" : "!t-bg-transparent"
      );

      return (
        <Button
          key={entity.id}
          type="button"
          block
          customType="ghost"
          onClick={() => onSelect(entity.id)}
          className={itemClassName}
          leftIcon={
            <CircularProgress
              value={progressById[entity.id] ?? 0}
              size={36}
              strokeWidth={3}
              hideLabel
              progressColor={colors.dark_green.DEFAULT}
            />
          }
        >
          <ToolTip text={entity.name}>
            <span className="t-min-w-0 t-flex-1 t-truncate t-text-left">
              {entity.name}
            </span>
          </ToolTip>
        </Button>
      );
    })}
  </div>
);

type EntityDropdownProps = {
  entities: SetupEntity[];
  selectedId: string;
  onSelect: (id: string) => void;
};

const EntityDropdown = ({
  entities,
  selectedId,
  onSelect,
}: EntityDropdownProps) => {
  const options = entities.map((entity) => ({
    label: entity.name,
    value: entity.id,
  }));

  const selectedOption = options.find((option) => option.value === selectedId);

  const handleChange = (
    option: MultiValue<OptionData> | SingleValue<OptionData>
  ): void => {
    if (option && "value" in option) onSelect(option.value);
  };

  return (
    <div className="t-flex t-shrink-0 t-items-center t-gap-2 t-px-4 t-pt-4">
      <span className="t-shrink-0 t-text-body t-text-text-60">Entity:</span>
      <div className="t-min-w-0 t-flex-1">
        <Combobox
          name="setupEntity"
          options={options}
          value={selectedOption}
          onChange={handleChange}
          isClearable={false}
          menuPortalTarget={document.body}
        />
      </div>
    </div>
  );
};

export const SetupModal = ({
  open,
  onClose,
  entities,
  seasons: fallbackSeasons = EMPTY_SEASONS,
  incompleteEntityIds = EMPTY_INCOMPLETE_IDS,
  focusEntityId = null,
}: SetupModalProps) => {
  const groupId = useGroupId();
  const { successToast, alertToast } = useToast();
  const { isMobile } = useWindowSize();

  const entityIds = useMemo(
    () => entities.map((entity) => entity.id),
    [entities]
  );

  const { progressById, overallProgress, probes } = useOnboardingProgress({
    groupId,
    entityIds,
  });

  const { data: groupEntities } = useGetEntitiesQuery();

  const { data: reviewEntities } = useGetGroupReviewDetailsQuery(
    { groupId },
    { skip: !groupId }
  );
  const { data: taxYears } = useGetTaxYearsQuery(
    { groupId },
    { skip: !groupId }
  );

  const [fallbackCurrentSeason] = fallbackSeasons;

  const [selectedEntityId, setSelectedEntityId] = useState(entities[0].id);

  const defaultSeasonFor = useCallback(
    (entityId: string): number => {
      const apiSeason = Number(
        taxYears?.entities.find((entity) => entity.uuid === entityId)
          ?.default_tax_year
      );

      return Number.isNaN(apiSeason) ? fallbackCurrentSeason : apiSeason;
    },
    [taxYears, fallbackCurrentSeason]
  );

  const currentSeason = defaultSeasonFor(selectedEntityId);

  const seasonsForEntity = useCallback(
    (entityId: string): number[] => {
      const taxYearEntity = taxYears?.entities.find(
        (entity) => entity.uuid === entityId
      );

      if (!taxYearEntity) return fallbackSeasons;

      const trackedYears = taxYearEntity.tracked_years.map(Number);

      return [defaultSeasonFor(entityId), ...trackedYears].sort(sortDesc);
    },
    [taxYears, fallbackSeasons, defaultSeasonFor]
  );

  const seasons = useMemo(
    () => seasonsForEntity(selectedEntityId),
    [seasonsForEntity, selectedEntityId]
  );

  const isMultiEntity = entities.length > 1;
  const isMultiYear = seasons.length > 1;

  const { data: detectedFilings, isFetching: isFetchingDetectedFilings } =
    useGetDetectedFilingsQuery(
      { groupId, entityId: selectedEntityId },
      { skip: !groupId || !selectedEntityId }
    );

  const panelSeasonFilings = groupFilingsBySeason(detectedFilings);

  const panelListRef = useRef<HTMLDivElement>(null);
  const questionsColumnRef = useRef<HTMLDivElement>(null);
  const lastQuestionKey = useRef<string | null>(null);

  const handleQuestionInteraction = useCallback(
    (event: React.SyntheticEvent<HTMLDivElement>): void => {
      const target = event.target as HTMLElement | null;
      const questionElement = target?.closest<HTMLElement>(QUESTION_SELECTOR);
      const questionKey = questionElement?.dataset.questionId;

      if (questionKey) lastQuestionKey.current = questionKey;
    },
    []
  );

  const getOriginRect = useCallback((): FlightRect | null => {
    const column = questionsColumnRef.current;
    if (!column) return null;

    const questionKey = lastQuestionKey.current;

    const question = questionKey
      ? column.querySelector<HTMLElement>(
          `[${QUESTION_ATTRIBUTE}="${questionKey}"]`
        )
      : null;

    return (question ?? column).getBoundingClientRect();
  }, []);

  const getTargetRect = useCallback(
    (filingId: string): FlightRect | null =>
      getFilingRowRect(panelListRef.current, filingId),
    []
  );

  const handleFlightQueued = useCallback((filingId: string): void => {
    scrollFilingRowIntoView(panelListRef.current, filingId);
  }, []);

  const handleFlightLanded = useCallback((filingId: string): void => {
    scrollFilingRowIntoView(panelListRef.current, filingId);
  }, []);

  const flatPanelFilings = panelSeasonFilings.flatMap(
    (seasonGroup) => seasonGroup.filings
  );

  const { activeFlight, landedFilingId, pendingFilingIds } = useFilingFlights({
    filings: flatPanelFilings,
    scopeKey: detectedFilings?.entity_id ?? "",
    isReady: Boolean(detectedFilings) && !isFetchingDetectedFilings,
    getOriginRect,
    getTargetRect,
    onFlightQueued: handleFlightQueued,
    onFlightLanded: handleFlightLanded,
  });

  const { data: filingsCount } = useGetDetectedFilingsCountQuery(
    { groupId },
    { skip: !groupId }
  );

  const selectedFilingsCount =
    filingsCount?.entities.find((entity) => entity.uuid === selectedEntityId)
      ?.detected_filings_count ?? 0;

  const {
    data: homepageData,
    isSuccess: isPrefillSuccess,
    isError: isPrefillError,
  } = useGetHomepageQuery(
    { groupId, entityId: selectedEntityId, seasons },
    { skip: !groupId || !selectedEntityId }
  );

  const isPrefillSettled = isPrefillSuccess || isPrefillError;

  const rawOnboardingEntities = (
    homepageData?.[currentSeason] as Record<string, unknown> | undefined
  )?.[TAX_ONBOARDING_ENTITIES_KEY];

  const onboardingEntities: OnboardingEntity[] = Array.isArray(
    rawOnboardingEntities
  )
    ? rawOnboardingEntities.map((entity) => {
        const item = entity as { uuid?: string; name?: string };
        return { entityId: item.uuid ?? "", name: item.name ?? "" };
      })
    : [];

  const wasOpen = useRef(false);

  useEffect(() => {
    const justOpened = open && !wasOpen.current;
    wasOpen.current = open;
    if (!justOpened) return;

    const hasFocusEntity = entities.some(
      (entity) => entity.id === focusEntityId
    );

    if (hasFocusEntity && focusEntityId) {
      setSelectedEntityId(focusEntityId);
      return;
    }

    setSelectedEntityId(incompleteEntityIds[0] ?? entities[0].id);
  }, [open, incompleteEntityIds, entities, focusEntityId]);

  const [putHomepage] = usePutHomepageMutation();
  const [upload7004FiledCopy] = useUpload7004FiledCopyMutation();

  const allSeasons = useMemo(() => {
    const unique = new Set<number>();

    entities.forEach((entity) =>
      seasonsForEntity(entity.id).forEach((season) => unique.add(season))
    );

    return [...unique].sort(sortDesc);
  }, [entities, seasonsForEntity]);

  const initialValues = useMemo(
    () => buildInitialValues(entities, allSeasons, currentSeason),
    [entities, allSeasons, currentSeason]
  );

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) onClose();
  };

  const handleTalkToSupport = () =>
    window.open(ONBOARDING_LINK, "_blank", "noopener,noreferrer");

  const handleUploadFiledCopy = async (file: File): Promise<void> => {
    try {
      await upload7004FiledCopy({
        groupId,
        entityId: selectedEntityId,
        season: currentSeason,
        file,
      }).unwrap();
      successToast({ message: "Filed copy uploaded" });
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        GENERIC_ERROR;
      alertToast({ message });
    }
  };

  const handleSubmit = async (values: TaxSetupValues): Promise<void> => {
    try {
      const payload = Object.fromEntries(
        seasons.map((season) => [
          season,
          buildScopePayload(
            resolveSeasonScope(values, selectedEntityId, season, currentSeason),
            season !== currentSeason
          ),
        ])
      );

      await putHomepage({
        groupId,
        entityId: selectedEntityId,
        payload,
      }).unwrap();

      successToast({ message: "Your details have been saved" });
      onClose();
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        GENERIC_ERROR;
      alertToast({ message });
    }
  };

  const primaryLabel =
    isMultiEntity || isMultiYear ? "Save and continue" : "Save details";

  const keepOpenOnSelectMenu = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (
      target?.closest?.("[class*='select__menu'], [class*='select__option']")
    ) {
      event.preventDefault();
    }
  };

  const selectedEntityName =
    entities.find((entity) => entity.id === selectedEntityId)?.name ??
    entities[0].name;

  const selectedEntity = groupEntities?.groups
    ?.flatMap((group) => group.entities ?? [])
    .find((entity) => entity.uuid === selectedEntityId);

  const reviewEntity = reviewEntities?.find(
    (entity) => entity.entityId === selectedEntityId
  );
  const reviewHomeState = reviewEntity?.states.find((state) => state.isHome);

  const selectedHomeStateId = reviewHomeState?.id ?? selectedEntity?.state_id;
  const selectedHomeState = reviewHomeState?.name ?? selectedEntity?.state;

  const pendingSeasons = seasons
    .filter(
      (season) =>
        !isSeasonComplete(homepageData?.[season], season, currentSeason)
    )
    .map(String);

  const defaultOpenSeasons =
    pendingSeasons.length > 0 ? pendingSeasons : [String(currentSeason)];

  const hasMissingRequired = (values: TaxSetupValues): boolean =>
    REQUIRED_QUESTION_IDS.some(
      (id) =>
        !isQuestionAnswered(
          values[`${scopePrefix(selectedEntityId, currentSeason)}${id}`]
        )
    );

  const renderQuestion = (season: number) => (question: TaxSetupQuestionType) =>
    (
      <div
        data-question-id={`${scopePrefix(selectedEntityId, season)}${
          question.id
        }`}
      >
        <TaxSetupQuestion
          question={question}
          isRequired={Boolean(question.isRequired) && season === currentSeason}
          namePrefix={scopePrefix(selectedEntityId, season)}
          textContext={{
            entityName: selectedEntityName,
            season,
            homeState: selectedHomeState,
          }}
          homeStateId={selectedHomeStateId}
          onFileUpload={handleUploadFiledCopy}
          entityId={selectedEntityId}
          season={season}
          onboardingEntities={onboardingEntities}
        />
      </div>
    );

  const renderSeasonQuestions = (season: number) => {
    const questions = questionsForSeason(season, currentSeason);

    if (season !== currentSeason) {
      return (
        <div className="t-flex t-flex-col t-gap-6">
          {questions.map((question) => (
            <div key={question.id}>{renderQuestion(season)(question)}</div>
          ))}
        </div>
      );
    }

    const namePrefix = scopePrefix(selectedEntityId, season);

    return (
      <TaxSetupSections
        key={namePrefix}
        questions={questions}
        namePrefix={namePrefix}
        renderQuestion={renderQuestion(season)}
      />
    );
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <Modal.Content
        size="fullscreen"
        useCustomOverlay
        onPointerDownOutside={keepOpenOnSelectMenu}
        onInteractOutside={keepOpenOnSelectMenu}
        onFocusOutside={keepOpenOnSelectMenu}
        className="t-flex t-h-full t-flex-col"
      >
        <Modal.Header className="!t-border-b-0 sm:t-pl-4">
          <Modal.Title>Complete your setup</Modal.Title>
          <Modal.Close />
        </Modal.Header>

        <SetupProgress value={overallProgress} />

        <FormikForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          className="t-flex t-min-h-0 t-flex-1 t-flex-col"
        >
          {({ isSubmitting, values }) => (
            <>
              <HomepagePrefill
                key={selectedEntityId}
                groupId={groupId}
                apiEntityId={selectedEntityId}
                scopeEntityId={selectedEntityId}
                seasons={seasons}
                currentSeason={currentSeason}
              />

              <HomepageAutosave
                key={selectedEntityId}
                groupId={groupId}
                apiEntityId={selectedEntityId}
                scopeEntityId={selectedEntityId}
                seasons={seasons}
                currentSeason={currentSeason}
              />

              {probes}

              <Modal.Body className="t-flex t-min-h-0 t-flex-1 t-overflow-hidden !t-p-0 sm:t-flex-col sm:t-overflow-y-auto">
                {isMultiEntity && isMobile && (
                  <EntityDropdown
                    entities={entities}
                    selectedId={selectedEntityId}
                    onSelect={setSelectedEntityId}
                  />
                )}

                {isMultiEntity && !isMobile && (
                  <EntityRail
                    entities={entities}
                    selectedId={selectedEntityId}
                    progressById={progressById}
                    onSelect={setSelectedEntityId}
                  />
                )}

                <div className="t-flex t-min-w-0 t-flex-1 t-gap-8 t-overflow-hidden t-p-6 sm:t-flex-none sm:t-flex-col sm:t-gap-4 sm:t-overflow-visible sm:t-p-4">
                  <div
                    ref={questionsColumnRef}
                    onPointerDownCapture={handleQuestionInteraction}
                    onFocusCapture={handleQuestionInteraction}
                    onChangeCapture={handleQuestionInteraction}
                    className="hide-scrollbar t-flex t-min-w-0 t-flex-1 t-flex-col t-gap-5 t-overflow-y-auto sm:t-flex-none sm:t-overflow-visible"
                  >
                    <p className="t-m-0 t-text-body t-text-neutral-50">
                      {MODAL_DESCRIPTION}
                    </p>

                    <SetupDocumentsBox
                      key={selectedEntityId}
                      entityId={selectedEntityId}
                    />

                    <SetupReviewBanner
                      key={`review-${selectedEntityId}`}
                      entityId={selectedEntityId}
                    />

                    {!isMultiYear &&
                      isPrefillSettled &&
                      renderSeasonQuestions(currentSeason)}

                    {isMultiYear && isPrefillSettled && (
                      <SeasonAccordion
                        key={selectedEntityId}
                        seasons={seasons}
                        defaultOpenSeasons={defaultOpenSeasons}
                        renderSeason={renderSeasonQuestions}
                      />
                    )}
                  </div>

                  <div className="t-w-[370px] t-shrink-0 sm:t-w-full">
                    <FilingsDetectedPanel
                      seasonFilings={panelSeasonFilings}
                      count={selectedFilingsCount}
                      entityId={selectedEntityId}
                      listRef={panelListRef}
                      landedFilingId={landedFilingId}
                      pendingFilingIds={pendingFilingIds}
                    />
                  </div>
                </div>

                <FilingFlightLayer flight={activeFlight} />
              </Modal.Body>

              <Modal.Footer className="t-flex t-items-center t-justify-between sm:t-flex-wrap sm:t-gap-2 sm:t-px-4">
                <Button
                  type="button"
                  customType="link_neutral"
                  leftIcon={<Headset color="currentColor" />}
                  onClick={handleTalkToSupport}
                >
                  Talk to support
                </Button>

                <div className="t-flex t-items-center t-gap-2">
                  <Button
                    type="button"
                    customType="secondary"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    customType="primary"
                    disabled={
                      !isPrefillSettled ||
                      isSubmitting ||
                      hasMissingRequired(values)
                    }
                    isLoading={isSubmitting}
                  >
                    {primaryLabel}
                  </Button>
                </div>
              </Modal.Footer>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
